import { useIneligibilityReasons } from '@shared/composables/useIneligibilityReasons';
import { ErrorType } from '@shared/constants/error';
import {
  type SiteContactResponseType,
  JobRoutesV1Repository,
  type JobResponseType,
  type JobProgramResponseType,
  type ProgramsType,
  type JobResponseWithIncentiveResultType,
  TaskStatusType,
  TaskOwnerType,
  JobStageType,
  TaskNameType,
  ActionOwnerType
} from '@shared/generated';
import { JobStageMap } from '@shared/types/stage';
import { isResponseError } from '@shared/utils/typeGuards';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useJobStore = defineStore('job', () => {
  const jobResponse = ref<JobResponseWithIncentiveResultType>();
  const loading = ref<boolean>(true);
  const fetchJobDetailsError = ref<ErrorType | null>(null);

  //Extracted from jobResponse
  const isCanceled = computed(() => {
    return jobResponse.value?.job.is_canceled;
  });
  const isComplete = computed(() => {
    return jobResponse.value?.job.stage === JobStageMap.COMPLETE;
  });
  const isGeotaggingRequired = computed(() => {
    return jobResponse.value?.job.is_geotagging_required;
  });
  const job = computed(() => {
    return jobResponse.value?.job as JobResponseType;
  });
  const jobPrograms = computed(() => {
    return jobResponse.value?.job.job_programs as JobProgramResponseType[];
  });
  const siteContact = computed(() => {
    return jobResponse.value?.job.site_contact as SiteContactResponseType;
  });
  const activeStage = computed(() => {
    return jobResponse.value?.job.stage as JobStageType;
  });

  const installerTasks = computed(() => {
    if (!jobResponse.value?.job.job_tasks) return [];
    return jobResponse.value.job.job_tasks.filter(
      (task) => task.action_owner === ActionOwnerType.INSTALLER
    );
  });

  const jobContractorDetails = computed(() => {
    return {
      name: jobResponse.value?.job.contractor_name,
      id: jobResponse.value?.job.contractor_id
    };
  });

  const optedInJobPrograms = computed(() => {
    //Only return opted in job programs
    return jobResponse.value?.job.job_programs?.filter(
      (jobProgram) => !jobProgram.opted_out_at
    ) as JobProgramResponseType[];
  });
  const programIds = computed(() => {
    return optedInJobPrograms.value?.map(
      (jobProgram) => jobProgram.program.id
    ) as string[];
  });

  const contractorExceptionTasks = computed(() => {
    return jobResponse.value?.job.job_tasks?.filter(
      (task) =>
        task.status === TaskStatusType.EXCEPTION &&
        task.owner === TaskOwnerType.CONTRACTOR
    );
  });
  const exceptionTasks = computed(() => {
    return jobResponse.value?.job.job_tasks?.filter(
      (task) => task.status === TaskStatusType.EXCEPTION
    );
  });

  const fetchJobDetails = async (jobId: string) => {
    loading.value = true;
    try {
      const res = await JobRoutesV1Repository.jobRoutesV1GetJob({
        jobId: jobId
      });
      jobResponse.value = res;
    } catch (error) {
      if (
        isResponseError(error) &&
        (error.status === 404 || error.status === 403 || error.status === 422)
      ) {
        fetchJobDetailsError.value = ErrorType.NOT_FOUND;
      } else {
        fetchJobDetailsError.value = ErrorType.UNEXPECTED;
      }
    } finally {
      loading.value = false;
    }
  };

  const setCurrentJob = (job: JobResponseType) => {
    jobResponse.value = { job, rebates_final: null };
  };

  const resetJobStore = () => {
    jobResponse.value = undefined;
    loading.value = true;
    fetchJobDetailsError.value = null;
  };

  const getCurrentJobProgramKey = () => {
    //TODO: review for stacking
    return jobPrograms.value[0].program.key as ProgramsType;
  };

  const getActiveJobProgramKeys = () => {
    if (!optedInJobPrograms.value) return [];

    return optedInJobPrograms.value.map(
      (jobProgram) => jobProgram.program.key as ProgramsType
    );
  };

  const getIneligibilityReasons = () => {
    //TODO: Remove once we have a compliance service and no longer rely on this form of ineligibilty
    if (!jobResponse.value?.job.job_programs) return;

    return useIneligibilityReasons(jobResponse.value?.job.job_programs);
  };

  const getIncompleteDependencies = (dependencies: TaskNameType[]) => {
    if (!jobResponse.value?.job.job_tasks) return;

    const hasSealedReviewTask = dependencies.includes(
      TaskNameType.SEALED_REVIEW
    );
    const incompleteDependencies = jobResponse.value.job.job_tasks
      .filter(
        (task) =>
          dependencies.includes(task.name) &&
          [
            TaskStatusType.READY,
            TaskStatusType.DISABLED,
            TaskStatusType.EXCEPTION
          ].includes(task.status)
      )
      .map((task) => task.name);

    if (!hasSealedReviewTask) {
      return incompleteDependencies;
    }
    return [...incompleteDependencies, TaskNameType.SEALED_REVIEW];
  };

  const getJobTaskById = (taskId: string) => {
    return jobResponse.value?.job.job_tasks?.find((task) => task.id === taskId);
  };

  return {
    loading,
    setCurrentJob,
    getCurrentJobProgramKey,
    getActiveJobProgramKeys,
    getIneligibilityReasons,
    fetchJobDetails,
    contractorExceptionTasks,
    exceptionTasks,
    getJobTaskById,
    resetJobStore,
    getIncompleteDependencies,
    activeStage,
    job,
    jobPrograms,
    isCanceled,
    isComplete,
    isGeotaggingRequired,
    siteContact,
    fetchJobDetailsError,
    jobContractorDetails,
    programIds,
    installerTasks,
    optedInJobPrograms
  };
});
